import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Modal } from 'react-bootstrap';
import { tail, compact, reduce } from 'lodash';
import Fa from 'react-fontawesome';
import { AgGridReact } from 'ag-grid-react';
import SearchField from '../../../../components/search-field';
import ProgressBar from '../../../../components/progress-bar';
import { massAssignMember } from '../../TeamActions';
import OwnerRenderer from '../../../../components/table-cells/SaleRepsNameRenderer';
import NumberDisplay from '../../../../components/number-display';

import styles from './styles.css';

function NotFoundRenderer() {
  return (<div className="text-danger"><Fa name="exclamation-triangle" /> Not found</div>);
}

function KeywordRenderer({ value }) {
  return (<code>{value}</code>);
}

class ImportMemberModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      submitting: false,
      results: null,
      hasFileReader: typeof window.FileReader !== 'undefined',
    };
    this.close = this.close.bind(this);
    this.open = this.open.bind(this);
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.ownerValueGetter = this.ownerValueGetter.bind(this);
    this.columnCellRendererSelector = this.columnCellRendererSelector.bind(this);
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.resizeColumns();
  }
  
  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  resizeColumns() {
    if (this.gridApi && this.gridColumnApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  columnCellRendererSelector(params) {
    if (!params || !params.data) {
      return null;
    }
    if (!params.data.found) {
      return {
        component: NotFoundRenderer,
      };
    }
    return {
      component: OwnerRenderer,
      params: {
        isOwner: true,
      },
    };
  }


  ownerValueGetter(params) {
    const { data } = params;
    if (!data || !data.found) {
      return null;
    }
    return `${data.first_name} ${data.last_name}`;
  }

  close() {
    this.setState({ open: false, submitting: false, results: null });
    this.props.onClose();
  }

  open() {
    this.setState({ open: true });
    this.props.onOpen();
  }

  handleFileChange(e) {
    if (this.state.submitting) {
      return;
    }
    this.setState({ submitting: true });
    const reader = new FileReader();
    reader.onload = () => {
      const content = reader.result ? reader.result.toString() : '';
      const data = compact(tail(content.split('\n')));

      const keywords = reduce(data, (items, datum) => {
        items.push(`${datum}`.trim().replace(/^\s+|\s+$|,/g, ''));
        return items;
      }, []);
      this.props.dispatch(massAssignMember(this.props.team._id, { keywords }))
        .then(
          ({ results }) => {
            if (this.state.open) {
              const resultData = reduce(results, (items, d, keyword) => {
                if (d) {
                  items.push({ keyword, ...d, found: true });
                } else {
                  items.push({ keyword, found: false });
                }
                return items;
              }, []);
              this.setState({ results: resultData }, () => {
                this.resizeColumns();
              });
            }
          },
          (e) => {

          },
        );
    };
    reader.readAsBinaryString(e.target.files[0]);
  }

  renderResults() {
    const { results } = this.state;
    const showPagination = results && results.length > 15;

    const columnDefs = [
      {
        headerName: 'Input',
        field: 'keyword',
        colId: 'keyword',
        pinned: 'left',
        suppressMenu: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        cellRenderer: KeywordRenderer,
      },
      {
        headerName: 'Sales Reps',
        colId: 'salesReps',
        suppressMenu: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        cellRendererSelector: this.columnCellRendererSelector,
        width: 250,
        valueGetter: this.ownerValueGetter,
      },
    ];

    return (
      <div>
        {showPagination &&
          <nav className="navbar navbar-default navbar-controls">
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
          </nav>
        }
        <div className="ag-theme-alpine" style={{ width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            domLayout="autoHeight"
            suppressColumnVirtualisation
            columnDefs={columnDefs}
            pagination={showPagination}
            paginationPageSize={15}
            onGridReady={this.onGridReady}
            rowData={results}
          />
        </div>
      </div>
    );
  }

  render() {
    const {
      submitting,
      open,
      hasFileReader,
      results,
    } = this.state;

    const { totalMembers, maximumMembers } = this.props;
    
    if (!hasFileReader) {
      return null;
    }
    if (totalMembers >= maximumMembers) {
      return null;
    }

    const hasResults = results !== null;

    return [
      (
        <Modal key="modal" show={open} onHide={this.close} keyboard={false} bsSize='lg'>
          <Modal.Header closeButton>
            <Modal.Title>Import Team Members</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {hasResults && this.renderResults()}
            {!hasResults &&
              <div>
                {!submitting &&
                  <div>
                    <div className="well">
                      <p>Import team members using an Excel CSV file.</p>
                      <p>
                        You can <span className={styles.uploader}><a href="">upload</a><input accept=".csv" disabled={submitting} id="csv" type="file" onChange={this.handleFileChange} /></span> an <code>.csv</code>
                        {' '}
                        file that contains the sales reps&rsquo; emails or User Id.
                      </p>
                      <p><a href="/assets/sample.csv" target="_blank">Click here</a> to download a sample <code>.csv</code> file.</p>
                      {totalMembers > 0 && <p>Current team size: <NumberDisplay value={totalMembers} precision={0} /> of <NumberDisplay value={maximumMembers} precision={0} /></p>}
                    </div>
                  </div>
                }
                {submitting &&
                  <div><ProgressBar /><p /></div>
                }
              </div>
            }
          </Modal.Body>
          <Modal.Footer>
            <div className='clearfix'>
              <Button
                bsStyle='link'
                onClick={this.close}
                disabled={submitting && !hasResults}
              >
                {hasResults ? 'Done' : 'Cancel'}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      ),
      (
        <Button
          key="button"
          bsStyle='primary'
          onClick={this.open}
          disabled={submitting}
        >
          <Fa name={submitting ? 'spinner' : 'upload'} spin={submitting} /> Import Members
        </Button>
      ),
    ];
  }
}

// Retrieve data from store as props
function mapStateToProps() {
  return {
  };
}

ImportMemberModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  team: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  totalMembers: PropTypes.number,
  maximumMembers: PropTypes.number,
};

ImportMemberModal.defaultProps = {
  onClose: () => {},
  onOpen: () => {},
  totalMembers: null,
  maximumMembers: null,
};

export default connect(mapStateToProps)(ImportMemberModal);
